import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedLayout } from "@/components/ProtectedLayout";

import { StoreNameDialog } from "@/components/onboarding/StoreNameDialog";

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Public pages - Critical: load sync
import Landing from "./pages/Landing";

// Public pages - Lazy load
const Auth = lazy(() => import("./pages/Auth"));
const PublicCatalog = lazy(() => import("./pages/PublicCatalog"));

const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Protected pages - All lazy loaded
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/Products"));
const ProductForm = lazy(() => import("./pages/ProductForm"));
const FittingRoom = lazy(() => import("./pages/FittingRoom"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Store = lazy(() => import("./pages/Store"));
const Credits = lazy(() => import("./pages/Credits"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const GenerateModels = lazy(() => import("./pages/GenerateModels"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: true,
    },
  },
});

/** Helper to wrap a page in ProtectedLayout */
const P = ({ children }: { children: React.ReactNode }) => (
  <ProtectedLayout>{children}</ProtectedLayout>
);

const App = () => (
  <div>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
            <Toaster />
            <Sonner />
            
            <BrowserRouter>
              <StoreNameDialog />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/loja/:slug" element={<PublicCatalog />} />
                  
                  
                  <Route path="/privacidade" element={<PoliticaPrivacidade />} />
                  <Route path="/termos" element={<TermosDeUso />} />
                  
                  {/* Protected routes */}
                  <Route path="/dashboard" element={<P><Dashboard /></P>} />
                  <Route path="/products" element={<P><Products /></P>} />
                  <Route path="/products/new" element={<P><ProductForm /></P>} />
                  <Route path="/products/:id" element={<P><ProductForm /></P>} />
                  <Route path="/fitting-room" element={<P><FittingRoom /></P>} />
                  <Route path="/gallery" element={<P><Gallery /></P>} />
                  <Route path="/store" element={<P><Store /></P>} />
                  <Route path="/credits" element={<P><Credits /></P>} />
                  <Route path="/tutorials" element={<P><Tutorials /></P>} />
                  <Route path="/generate-models" element={<P><GenerateModels /></P>} />
                  
                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </div>
);

export default App;
